import React, { useEffect, useState } from 'react';

import { Box, CardContent, Grid, Typography, Link, Card, Slider, Skeleton } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { COLOR_PRIMARY, COLOR_TOOLTIP_BACKGROUND, exchangeRates } from 'src/const';
import { CustomIconButton, InfoButton } from 'src/App/Components/Buttons';
import { formatCurrency } from 'src/utils/functions';
import { reportPageStyles as styles } from '../styles';
import {
  COLOR_GREY_200,
  COLOR_GREY_600,
  COLOR_GREY_800,
  COLOR_TEXT_1,
  COLOR_TEXT_4,
} from 'src/colors';
import EditButton from 'src/App/Components/Buttons/EditButton';
import { InputTextField } from 'src/App/Components/Inputs';

const TooltipContent = ({ tooltipConfig, selectedCompsCount }) => {
  return (
    <Card>
      <CardContent sx={{ backgroundColor: COLOR_TOOLTIP_BACKGROUND, p: 2, maxWidth: 400 }}>
        <Grid container spacing={2} direction="column">
          {/* Title and Time Period Row */}
          <Grid item>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h6" fontWeight="bold">
                  {tooltipConfig.name}
                </Typography>
              </Grid>
              <Grid item>
                {tooltipConfig.timePeriod && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <EventIcon fontSize="small" />{' '}
                    {tooltipConfig.timePeriod && tooltipConfig.timePeriod == 'ltm'
                      ? '2023 November - 2024 October'
                      : tooltipConfig.timePeriod}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Definition */}
          <Grid item>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {tooltipConfig.definition.length > 0 &&
                tooltipConfig.definition.map((line, index) => (
                  <Typography key={index} variant="body2">
                    {line}
                  </Typography>
                ))}
            </Box>
          </Grid>

          {/* Calculation Section */}
          {tooltipConfig.formula && (
            <Grid item>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 'bold', borderBottom: `1px solid ${COLOR_GREY_800}`, pb: 0.5 }}
              >
                Calculation:
              </Typography>
              <Typography variant="body2">{tooltipConfig.formula}</Typography>
            </Grid>
          )}

          {/* Source */}
          {tooltipConfig.source && (
            <Grid item>
              <Typography variant="caption">
                *Source: {tooltipConfig.source.replace(' n ', ' ' + selectedCompsCount + ' ')}
              </Typography>
            </Grid>
          )}

          {/* Call to Action Link */}
          <Grid item>
            <Box mt={1}>
              <Link
                href={tooltipConfig.ctaURL}
                target="_blank"
                rel="noopener"
                sx={{ fontWeight: 'bold', textDecoration: 'none' }}
              >
                <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                  {tooltipConfig.ctaText}
                </Typography>
              </Link>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const KPICard = ({
  kpiKey,
  kpiName,
  value,
  tooltipConfig,
  unitType,
  currency,
  isUserReportOwner,
  isEditable,
  editMode,
  handleEditModalOpen,
  valuePercentiles,
  hanldeKpiEdit,
  selectedCompsCount,
}: {
  kpiKey: string;
  kpiName: string;
  value: number | React.ReactElement | 'loading' | null;
  tooltipConfig: {
    name: string;
    timePeriod: string;
    source: string;
    formula?: string;
    definition: string[];
    ctaText: string;
    ctaURL: string;
  } | null;
  unitType: string;
  currency: string;
  isUserReportOwner: boolean;
  isEditable: boolean;
  editMode: 'scroll' | 'modal' | null;
  handleEditModalOpen: () => void;
  valuePercentiles: {
    25: number;
    50: number;
    75: number;
    90: number;
  };
  hanldeKpiEdit: (key: string, value: any) => void;
  selectedCompsCount: number;
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));

  const [inLineEditToggled, setInLineEditToggled] = useState(false);
  const inLineEditConfig = {
    min: Math.round(valuePercentiles[25] * 0.9),
    max: Math.round(valuePercentiles[75] * 1.1),
    marks: [
      {
        value: valuePercentiles[25],
        label: '25th',
        isLabelHidden: false,
      },
      {
        value: valuePercentiles[50],
        label: '50th',
        isLabelHidden: false,
      },
      {
        value: valuePercentiles[75],
        label: '75th',
        isLabelHidden: false,
      },
    ],
  };
  const [editValue, setEditValue] = useState<number>(value as number);

  useEffect(() => {
    setEditValue(value as number);
  }, [value]);

  return (
    <Grid
      container
      item
      xs={inLineEditToggled || matches ? 12 : 6}
      justifyContent={'center'}
      alignItems={'center'}
      sx={{ padding: '5px' }}
    >
      <Grid
        container
        item
        xs={12}
        justifyContent={'center'}
        alignContent={'center'}
        alignItems={'center'}
        sx={{ ...styles.paper, height: inLineEditToggled ? '200px' : '140px', padding: '20px' }}
      >
        <>
          <Grid item xs={12} alignItems={'center'} sx={{ marginBottom: '10px' }}>
            <Typography variant="body1" sx={{ fontSize: '16px', color: COLOR_TEXT_4 }}>
              {kpiName}{' '}
              {tooltipConfig && (
                <InfoButton
                  tooltipContent={
                    <TooltipContent
                      tooltipConfig={tooltipConfig}
                      selectedCompsCount={selectedCompsCount}
                    />
                  }
                  iconColor={COLOR_TEXT_4}
                />
              )}
            </Typography>
          </Grid>
          {!inLineEditToggled ? (
            <Grid
              item
              xs={12}
              container
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              {value == 'loading' ? (
                <Skeleton
                  variant="text"
                  sx={{ fontSize: '5rem', borderRadius: 4, width: '100%' }}
                />
              ) : value != null ? (
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLOR_TEXT_1 }}>
                  {unitType === '$' && exchangeRates[currency].symbol}
                  {unitType === '$' && typeof value == 'number'
                    ? formatCurrency(String(value))
                    : value}
                  {unitType === '%' && typeof value == 'number' && '%'}
                </Typography>
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLOR_TEXT_1 }}>
                  {' '}
                  -{' '}
                </Typography>
              )}
              {isEditable && (
                <EditButton
                  isDisabled={!isUserReportOwner}
                  actionOnClick={() => {
                    if (editMode === 'modal') {
                      handleEditModalOpen();
                    } else if (editMode === 'scroll') {
                      setInLineEditToggled(true);
                    } else {
                      console.error('Edit mode not set');
                    }
                  }}
                  isLocked={false}
                />
              )}
            </Grid>
          ) : (
            <Grid container item xs={12} direction="column">
              <Grid
                item
                xs={12}
                container
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={{ marginBottom: '10px' }}
              >
                <Grid item xs={7}>
                  <InputTextField
                    field={{ key: kpiName, label: kpiName, unit: unitType }}
                    stateObj={{ [kpiName]: editValue }}
                    handleValueChange={(_, value) => {
                      setEditValue(value);
                    }}
                    currency={currency}
                    disabled={false}
                    hideLabel={true}
                  />
                </Grid>
                <Grid container item xs={4} direction="row" gap={1} justifyContent="flex-end">
                  <CustomIconButton
                    actionOnClick={() => setInLineEditToggled(false)}
                    Icon={<CloseIcon />}
                    title="Cancel"
                    backgroundColor={COLOR_GREY_200}
                    iconColor={COLOR_GREY_600}
                    hoverBackgroundColor="#222"
                    hoverIconColor="white"
                  />
                  <CustomIconButton
                    actionOnClick={() => {
                      hanldeKpiEdit(kpiKey, editValue);
                      setInLineEditToggled(false);
                    }}
                    Icon={<DoneRoundedIcon />}
                    title="Apply"
                    backgroundColor={COLOR_PRIMARY}
                    iconColor="white"
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Slider
                  size="medium"
                  value={editValue}
                  min={inLineEditConfig.min}
                  max={inLineEditConfig.max}
                  marks={inLineEditConfig.marks
                    .reverse()
                    .map((mark, index, array) => {
                      if (index > 0) {
                        const prevMark = array[index - 1];
                        const isTooClose =
                          Math.abs((mark.value - prevMark.value) / mark.value) < 0.05;
                        return { ...mark, isLabelHidden: isTooClose };
                      }
                      return { ...mark, isLabelHidden: false };
                    })
                    .map(mark => ({
                      ...mark,
                      label: (
                        <Typography
                          variant="caption"
                          sx={{
                            color: COLOR_GREY_600,
                            display: mark.isLabelHidden ? 'none' : 'block',
                          }}
                        >
                          {mark.label}
                        </Typography>
                      ),
                    }))}
                  onChange={(event, value) => {
                    setEditValue(value as number);
                  }}
                  valueLabelDisplay="off"
                  sx={{
                    color: COLOR_PRIMARY,
                    ...styles.slider,
                  }}
                />
              </Grid>
            </Grid>
          )}
        </>
      </Grid>
    </Grid>
  );
};

export default KPICard;
